import { FileText } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import type { DocumentRow } from '../api/types'
import { DocumentStatusChip } from './DocumentStatusChip'

export interface DocumentCardProps {
  row: DocumentRow
  selected?: boolean
  onClick?: (row: DocumentRow) => void
}

/**
 * Grid-mode card per foundation §Archetype 4. 160×200px, thumbnail on
 * top (generic PDF icon in v1 — real thumbnail rendering is future
 * scope), metadata on bottom, status chip overlay.
 */
export function DocumentCard({ row, selected, onClick }: DocumentCardProps) {
  const hasAnyDoc = row.vendor_cofa !== null || row.spi_cofa !== null
  return (
    <button
      type="button"
      onClick={() => onClick?.(row)}
      aria-label={`${row.lot_name} — ${row.product_name}`}
      data-selected={selected || undefined}
      className={cn(
        'group relative flex h-[200px] w-[160px] flex-shrink-0 cursor-pointer flex-col overflow-hidden rounded-sm border border-border-subtle bg-surface text-left transition-[border-color,box-shadow] duration-fast',
        'hover:border-border-default hover:shadow-sm',
        selected && 'border-accent shadow-[0_0_0_1px_var(--color-accent)]',
      )}
    >
      <div className="flex h-[120px] items-center justify-center border-b border-border-subtle bg-surface-sunk">
        <FileText
          size={48}
          className={cn(
            'text-tertiary transition-colors duration-fast group-hover:text-secondary',
            !hasAnyDoc && 'opacity-40',
          )}
          aria-hidden="true"
        />
        <span className="absolute right-1.5 top-1.5">
          <DocumentStatusChip status={row.status} />
        </span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-0.5 p-2">
        <span
          className="break-words text-2xs font-semibold text-primary"
          title={row.lot_name}
        >
          {row.lot_name}
        </span>
        <span className="line-clamp-2 text-xs leading-tight text-primary">
          {row.product_name}
        </span>
        <span className="mt-auto truncate text-2xs text-tertiary">
          {row.vendor_name || '(no vendor)'}
        </span>
      </div>
    </button>
  )
}
